import React, { useState } from 'react';
import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { labelClass, sectionTitleClass } from 'modules/Common/form/v2';

// Shared inspector-rail primitives for the Portal Studio, promoted out of the
// supplier inspector so the operator surface builds its panels on the same
// row/group vocabulary.
//
// Deliberate style rules (they are the fix for the old stacked-cards layout):
//  - ONE label treatment for everything (`labelClass`, the same 10px uppercase
//    label the v2 text fields render). Toggles are never given CgToggle's
//    on/off labels, which are `text-sm/base font-semibold` + `px-4` and used
//    to shout louder than the settings they belonged to.
//  - Groups are separated by hairlines inside the single rail card, never by
//    nested cards with their own border + p-6.
export const TOGGLE_SIZING = 'sm';

export function CopyLinkButton({ text, label = 'Copy link', size = 'sm', variant = 'outline' }) {
  const [copied, setCopied] = useState(false);
  return (
    <CgRadixButton
      type="button"
      variant={copied ? 'secondary' : variant}
      size={size}
      icon={copied ? faCheck : faCopy}
      className="shrink-0"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          /* clipboard unavailable — ignore */
        }
      }}
    >
      {copied ? 'Copied!' : label}
    </CgRadixButton>
  );
}

// A hairline-separated group inside the rail card. `stack` spaces plain
// FormFields, which carry no margin of their own (they normally sit in
// FormSection's gap-4 grid).
export function Group({ title, children, first = false, stack = false }) {
  return (
    <section className={first ? '' : 'border-t border-slate-100 pt-4 mt-4'}>
      {title ? <p className={sectionTitleClass}>{title}</p> : null}
      {stack ? <div className="flex flex-col gap-3">{children}</div> : children}
    </section>
  );
}

// Label + hint on the left, control on the right — the rail's row unit for
// anything that isn't a full-width input.
export function ControlRow({ title, hint, children, emphasis = false }) {
  return (
    <div
      className={`flex items-start justify-between gap-3 ${
        emphasis ? 'rounded-xl bg-slate-50 border border-slate-200 p-3' : ''
      }`}
    >
      <div className="min-w-0">
        {emphasis ? (
          <p className="text-[13px] font-semibold text-slate-800 m-0">{title}</p>
        ) : (
          <span className={`${labelClass} !mb-0.5`}>{title}</span>
        )}
        {hint ? <p className="text-[11px] text-slate-500 m-0 mt-0.5 leading-snug">{hint}</p> : null}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
